/**
 * Jediné místo, kde se z média odvozuje hodnota ořezu — `gravity` pro adresu obrázkové
 * služby i `object-position` pro CSS. Rozhraní, web i admin ji odsud berou; nikdo jiný ji
 * neskládá (hlídá `node scripts/orez/orez.mjs kod --tree <strom>`).
 *
 * Režim ořezu (`focalSource`):
 * - `"auto"` — bod nikdo neurčil; souřadnice je jen výchozí střed. Ořez dělá obrázková
 *   služba sama podle obsahu (`gravity=auto`), CSS nechává střed.
 * - `"ai"` — účinný bod je AI bod (hlavní motiv podle modelu, `aiFocalPointX/Y`).
 * - `"manual"` — redakce bod výslovně nastavila (i kdyby ležel přesně na středu). Ruční bod
 *   má přednost: AI bod se u ruční fotky ukládá taky, účinný bod ale nemění.
 *
 * Invariant: `focalSource = "ai"` ⇒ AI bod není null a rovná se účinnému bodu
 * (`focalPointX/Y`). Stav `ai` nastavuje jen server (`applyAiFocal`, `applyFocalPatch`).
 *
 * Proč dva povrchy: Cloudflare Image Transformations ořezává jen tehdy, když adresa nese
 * OBA rozměry. Adresa jen se šířkou (srcset, `url`) neořezává nic, výřez tam dělá až CSS
 * `object-cover` — a tam se bod uplatní přes `object-position`.
 */

export const FOCAL_CENTER = 0.5;

export const FOCAL_SOURCES = ["auto", "ai", "manual"] as const;
export type FocalSource = (typeof FOCAL_SOURCES)[number];

/** Minimální strukturální tvar média, aby modul nemusel znát `MediaResponse`. */
export interface FocalLike {
  /** Účinný bod (čte web i admin). */
  focalPointX: number;
  focalPointY: number;
  focalSource: FocalSource;
  /** AI bod; `null` nebo chybí = AI bod nemá. */
  aiFocalPointX?: number | null;
  aiFocalPointY?: number | null;
}

export function isManualFocal(m: FocalLike): boolean {
  return m.focalSource === "manual";
}

/** Má fotka bod, podle kterého se ořezává (ruční nebo AI)? `auto` ne. */
export function hasFocalPoint(m: FocalLike): boolean {
  return m.focalSource === "manual" || m.focalSource === "ai";
}

function focalPoint(m: FocalLike): string {
  return `${m.focalPointX}x${m.focalPointY}`;
}

const CENTER_POINT = `${FOCAL_CENTER}x${FOCAL_CENTER}`;

/**
 * Hodnota parametru `gravity` pro adresu obrázku.
 *
 * - `crops: true` — adresa nese oba rozměry, takže ořezává: s bodem (ruční nebo AI) jeho
 *   souřadnice, bez něj `"auto"`.
 * - `crops: false` — adresa jen se šířkou: ruční bod jako dřív, `auto` i `ai` střed. Na
 *   takové adrese je gravitace mrtvý parametr (bez `height` se neořezává), drží se jen proto,
 *   aby se adresa nezměnila a knihovna se znovu nenaúčtovala — proto ji AI bod nemění (F8.2).
 *   Odstranit ji je follow-up po doložení placeného tarifu obrázků (PRD řezu 7, „Mrtvá
 *   gravitace na adresách bez ořezu“).
 */
export function focalGravity(m: FocalLike, opts: { crops: boolean }): string {
  if (opts.crops) return hasFocalPoint(m) ? focalPoint(m) : "auto";
  return isManualFocal(m) ? focalPoint(m) : CENTER_POINT;
}

function percent(v: number): string {
  return `${Math.round(v * 1000) / 10}%`;
}

/** `object-position` z libovolného bodu, bez rozhodnutí o režimu (živý náhled v adminu). */
export function focalObjectPositionFromPoint(x: number, y: number): string {
  return `${percent(x)} ${percent(y)}`;
}

/**
 * `object-position` pro `<img>` s CSS ořezem: jen s bodem (ruční nebo AI), jinak `undefined`
 * (atribut nevzniká — nezvětšuje HTML stránek o nic, co by platilo i bez něj).
 */
export function focalObjectPosition(m: FocalLike): string | undefined {
  return hasFocalPoint(m) ? focalObjectPositionFromPoint(m.focalPointX, m.focalPointY) : undefined;
}

/**
 * Gravitace pro náhled 400×400, který generuje binding obrázků (pipeline při nahrání
 * i regenerace po změně bodu). Binding s oběma rozměry ořezává VŽDY, takže je to týž
 * případ jako `focalGravity(m, { crops: true })`, jen ve tvaru, který binding bere.
 */
export function focalBindingGravity(
  m: FocalLike,
): "auto" | { x: number; y: number; mode: "box-center" } {
  return hasFocalPoint(m) ? { x: m.focalPointX, y: m.focalPointY, mode: "box-center" } : "auto";
}

/**
 * Vstup `applyFocalPatch` — pole bodu z těla `PATCH`, všechna volitelná. Stav `ai` tělo
 * nastavit nemůže (`MediaUpdateSchema`), AI bod tělo nemění.
 */
export interface FocalPatch {
  focalPointX?: number;
  focalPointY?: number;
  focalSource?: Exclude<FocalSource, "ai">;
}

/** AI bod fotky, když ho má (obě osy), jinak `null`. */
function aiPointOf(m: FocalLike): { x: number; y: number } | null {
  return m.aiFocalPointX != null && m.aiFocalPointY != null
    ? { x: m.aiFocalPointX, y: m.aiFocalPointY }
    : null;
}

/** Má fotka AI bod (obě osy)? Na tom stojí invariant `ai` ⇒ AI bod i stav `unchanged`. */
export function hasAiFocalPoint(m: FocalLike): boolean {
  return aiPointOf(m) !== null;
}

/** Pole AI bodu z `current` beze změny; chybí-li v `current` obě, nepřidá nic. */
function carryAi(m: FocalLike): Pick<FocalLike, "aiFocalPointX" | "aiFocalPointY"> {
  if (m.aiFocalPointX === undefined && m.aiFocalPointY === undefined) return {};
  return { aiFocalPointX: m.aiFocalPointX ?? null, aiFocalPointY: m.aiFocalPointY ?? null };
}

/**
 * Stav bodu po uložení média. Pravidlo: režim mění JEN výslovné `focalSource`, nikdy
 * pouhá přítomnost souřadnic — admin přikládá souřadnice ke každému uložení zásuvky
 * (i k opravě alt textu), takže „přítomnost ⇒ ruční“ by časem překlopilo do ručního
 * režimu všechno, čeho se kdo dotkl.
 *
 * - `"manual"` — ruční režim s poslaným bodem (chybějící osa zůstává), i na středu.
 * - `"auto"` („Zpět na automatický“) — s AI bodem účinný bod = AI bod a stav `ai`; bez něj
 *   bod zpět na střed a stav `auto`. Poslané souřadnice se nepoužijí.
 * - bez `focalSource` — souřadnice přijme jen ruční médium; automatické a AI médium je
 *   nemění (jejich bod neurčuje redakce).
 *
 * AI bod patch nikdy nemění; výsledek ho nese, jak byl v `current`.
 */
export function applyFocalPatch(current: FocalLike, patch: FocalPatch): FocalLike {
  const ai = carryAi(current);
  if (patch.focalSource === "manual" || (!patch.focalSource && isManualFocal(current))) {
    return {
      focalPointX: patch.focalPointX ?? current.focalPointX,
      focalPointY: patch.focalPointY ?? current.focalPointY,
      focalSource: "manual",
      ...ai,
    };
  }
  if (patch.focalSource === "auto") {
    const point = aiPointOf(current);
    return point
      ? { focalPointX: point.x, focalPointY: point.y, focalSource: "ai", ...ai }
      : { focalPointX: FOCAL_CENTER, focalPointY: FOCAL_CENTER, focalSource: "auto", ...ai };
  }
  return {
    focalPointX: current.focalPointX,
    focalPointY: current.focalPointY,
    focalSource: current.focalSource,
    ...ai,
  };
}

/**
 * Stav bodu po výsledku AI (nahrání, `ai-refresh`).
 *
 * - AI bod → uloží se vždy; ruční fotka účinný bod ani stav nemění, jinak účinný bod = AI
 *   bod a stav `ai`.
 * - `null` (AI bod nezjistilo) → fotka `ai` se vrátí na střed a `auto` s AI bodem null;
 *   ostatní se nemění.
 */
export function applyAiFocal(current: FocalLike, ai: { x: number; y: number } | null): FocalLike {
  if (ai) {
    const stored = { aiFocalPointX: ai.x, aiFocalPointY: ai.y };
    return isManualFocal(current)
      ? {
          focalPointX: current.focalPointX,
          focalPointY: current.focalPointY,
          focalSource: "manual",
          ...stored,
        }
      : { focalPointX: ai.x, focalPointY: ai.y, focalSource: "ai", ...stored };
  }
  if (current.focalSource === "ai") {
    return {
      focalPointX: FOCAL_CENTER,
      focalPointY: FOCAL_CENTER,
      focalSource: "auto",
      aiFocalPointX: null,
      aiFocalPointY: null,
    };
  }
  return {
    focalPointX: current.focalPointX,
    focalPointY: current.focalPointY,
    focalSource: current.focalSource,
    ...carryAi(current),
  };
}
